import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, OnInit, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, Subscriber } from 'rxjs';
import { delay, finalize, map, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { DialogService } from '../dialog/dialog.service';
import { EnvironmentService } from '../environment/environment.service';
import { ErrorService } from '../error/error.service';
import { PlatformService } from '../platform/platform.service';
import { StorageService } from '../storage/storage.service';
import { ITokens } from './models/tokens.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnInit {

  private isCordova: boolean = this.platformService.isCordova();
  public isAuthenticatedSignal = signal(false);
  private logger: Logger;
  public  AuthAccessToken: string = 'AuthAccessToken';
  public  AuthRefreshToken: string = 'AuthRefreshToken';
  public  TokenTimeStamp: string = 'TokenTimeStamp';
  private TokenExpiresIn: string = 'AuthTokenTimeout'; // key-variable named after server naming(expires_in) for better readability. 'AuthTokenTimeout' string is used for persitence between ionic-release-app(this) and admin-app
  private authTokenKeys: string[] = [this.AuthAccessToken, this.AuthRefreshToken, this.TokenExpiresIn, this.TokenTimeStamp];
  private httpErrorResponse: HttpErrorResponse;

  private appLink: string;
  private endPointAuthorization: string;
  private endpointCodeExchange: string;
  private endpointLogOut: string;
  private environmentService: EnvironmentService;

  constructor(
    private dialogService: DialogService,
    private errorService: ErrorService,
    private httpClient: HttpClient,
    private injector: Injector,
    private loggingService: LoggingService,
    private platformService: PlatformService,
    private storageService: StorageService
  ) {
    this.logger = loggingService.getLogger("[AuthService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit(): void {
  }

  public removeStorageData(): Observable<void> {
    this.logger.info('removeStorageData()');
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.storageService.removeStoredItems(this.authTokenKeys).subscribe(() => {
        this.isAuthenticatedSignal.set(false);
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  public setStorageData(tokens: ITokens): Observable<void> {
    this.logger.info('setStoreageData()');
    const access_token = tokens.access_token;
    const refresh_token = tokens.refresh_token;
    const expires_in = tokens.expires_in;
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.storageService.addItemToLocalStorage(this.AuthRefreshToken, refresh_token, 'setStorageData()').subscribe(() => {
        console.log('## AuthService setStorageData() SET refreshToken');
        this.isAuthenticatedSignal.set(true);
        this.storageService.addItemToLocalStorage(this.AuthAccessToken, access_token, 'setStorageData()').subscribe(() => {
          console.log('## AuthService setStorageData() SET accessToken');
          this.storageService.addItemToLocalStorage(this.TokenTimeStamp, Date.now(), 'setStorageData()').subscribe(() => {
            console.log('## AuthService setStorageData() SET tokenTimeStamp');
            this.storageService.addItemToLocalStorage(this.TokenExpiresIn, expires_in, 'setStorageData()').subscribe(() => {
              console.log('## AuthService setStorageData() SET expireToken');
              subscriber.next();
              subscriber.complete();
            });
          });
        });
      });
    });
  }

  public verifyUserCredentials(): Observable<void> {
    this.logger.info('verifyUserCredentials() get refreshToken from storage');
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'verifyUserCredentials()').subscribe((refreshToken: string) => {
        console.log('## AuthService verifyUserCredentials() GET refreshToken');
        if (refreshToken) {
          this.isAuthenticatedSignal.set(true);
          subscriber.next();
          subscriber.complete();
        } else {
          this.logger.info('verifyUserCredentials(): No stored refreshToken');
          subscriber.error();
          subscriber.complete();
        }
      });
    });
  }

  public authenticateSignedInUser(signInCode: string, tenantDomain: string): Observable<void> {
    this.logger.info('authenticateSignedInUser()');

    if (!this.environmentService) {
      this.environmentService = this.injector.get(EnvironmentService);
    }
    this.appLink = this.environmentService.getEnvironmentEndpoint['appLink'];                             // 'printixapp://'
    this.endPointAuthorization = this.environmentService.getEnvironmentEndpoint['endPointAuthorization']; // 'grant_type=authorization_code&client_id=web_app&client_secret=1234&redirect_uri=',
    this.endpointCodeExchange = this.environmentService.getEnvironmentEndpoint['endpointCodeExchange'];   // 'https://auth.printix.net/oauth/token'
    let returnDomain: string = this.isCordova ? encodeURIComponent(this.appLink + '?' + tenantDomain) : location.origin; // 'printixapp://' + '?' + 'abc.printix.net' : location.origin
    this.logger.info('## AuthService authenticateSignedInUser() returnDomain: ' + returnDomain);
    let dataToSend: string = this.endPointAuthorization + returnDomain + '&code=' + signInCode;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return new Observable((observer) => {
      this.httpClient.post<ITokens>(this.endpointCodeExchange, dataToSend, { headers: headers })
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(1),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          const unauthenticatedUser =
            this.httpErrorResponse.status === 401 || this.httpErrorResponse.status === 403
            ? true
            : false;

          if (!unauthenticatedUser) {
            this.errorService.handleHttpClientResponseError(this.httpErrorResponse, 'GET', '[AuthService] authenticateSignedInUser()');
          }
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((tokens: ITokens) => {
        this.setStorageData(tokens).subscribe(() => {
          observer.next();
          observer.complete();
        });
      });
    });
  }

  public refreshAccessToken(): Observable<string> {
    this.logger.info('### refreshAccessToken()');
    return new Observable((observer) => {
      this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'refreshAccessToken()').subscribe((tokenRefresh: string) => {
        if (tokenRefresh) {
          this.isAuthenticatedSignal.set(true);
          if (!this.environmentService) {
            this.environmentService = this.injector.get(EnvironmentService);
          }
          this.endpointCodeExchange = this.environmentService.getEnvironmentEndpoint['endpointCodeExchange']; // 'https://auth.printix.net/oauth/token'
          const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          });
          let dataToSend: string = "grant_type=refresh_token&client_id=web_app&client_secret=thehouseisonfire&refresh_token=" + tokenRefresh;
          this.httpClient.post<ITokens>(this.endpointCodeExchange, dataToSend, { headers: headers })
          .pipe(retryWhen(error => error.pipe(
            delay(1000),
            take(1),
            tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
            finalize(() => {
              observer.error(this.httpErrorResponse);
              observer.complete();
            })
          )))
          .subscribe((tokens: ITokens) => {
            this.setStorageData(tokens).subscribe(() => {
              this.storageService.getItemFromLocalStorage(this.AuthAccessToken, 'refreshAccessToken()').subscribe((accessToken: string) => {
                observer.next(accessToken);
                observer.complete();
              });
            });
          });
        } else {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  public getAccessToken(url?): Observable<string> {
    this.logger.info('getAccessToken()');
    return new Observable((observer) => {
      this.checkIfAccessTokenIsValid(url).subscribe((isTokenUsable: boolean) => {
        this.logger.info('getAccessToken() isTokenUsable: ' + isTokenUsable);
        if (isTokenUsable) {
          this.storageService.getItemFromLocalStorage(this.AuthAccessToken, 'getAccessToken()').subscribe((accessToken: string) => {
            if (accessToken) {
              observer.next(accessToken);
              observer.complete();
            } else {
              this.logger.info('Missing accessToken - refreshing token');
              this.refreshAccessToken().subscribe((accessToken: string) => {
                observer.next(accessToken);
                observer.complete();
              });
            }
          });
        } else {
          this.refreshAccessToken().subscribe((accessToken: string) => {
            observer.next(accessToken);
            observer.complete();
          });
        }
      }, (error) => {
        this.logger.info('Error on checkIfAccessTokenIsValid inside getAccessToken()');
        observer.complete();
      });
    });
  }

  private checkIfAccessTokenIsValid(url): Observable<boolean> {
    this.logger.info('checkIfAccessTokenIsValid() for url: ' + url);

    const now = Date.now();

    return this.storageService.getItemFromLocalStorage(this.TokenExpiresIn, 'checkIfAccessTokenIsValid').pipe(
      switchMap((tokenExpiresIn: number) => {
        if (!tokenExpiresIn) {
          return of(false);
        }
        return this.storageService.getItemFromLocalStorage(this.TokenTimeStamp, 'checkIfAccessTokenIsValid').pipe(
          map((tokenTimeStamp: number) => {
            if (!tokenTimeStamp) {
              return false;
            }
            const tokenTimeOutAdjusted = tokenExpiresIn - 20;
            const isTokenValid = (tokenTimeStamp + tokenTimeOutAdjusted * 1000) > now;
            return isTokenValid;
          })
        );
      })
    );
  }

  public deAuthenticateUser(): Observable<void> {
    this.logger.info('deAuthenticateUser()');

    return new Observable((observer) => {
      this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'deAuthenticateUser()').subscribe((tokenRefresh: string) => {
        if (tokenRefresh) {
          if (!this.environmentService) {
            this.environmentService = this.injector.get(EnvironmentService);
          }
          this.endpointLogOut = this.environmentService.getEnvironmentEndpoint['endpointLogOut'];
          const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          });
          this.httpClient.get<void>(this.endpointLogOut + tokenRefresh, { headers: headers })
          .pipe(retryWhen(error => error.pipe(
            delay(1000),
            take(3),
            tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
            finalize(() => {
              if (this.httpErrorResponse.status === 401 || this.httpErrorResponse.status ===  403) {
                this.logger.info('deAuthenticateUser() httpErrorResponse === ' + this.httpErrorResponse.status);
              } else {
                this.errorService.handleHttpClientResponseError(this.httpErrorResponse, 'GET', '[AuthService] deAuthenticateUser()');
              }
              observer.error(this.httpErrorResponse);
              observer.complete();
            })
          )))
          .subscribe(() => {
            this.removeStorageData().subscribe(() => {
              observer.next();
              observer.complete();
            });
          });
        } else {
          this.removeStorageData().subscribe(() => {
            observer.next();
            observer.complete();
          });
        }
      });
    });
  }

  public refreshAccessTokenFromUrl(tokenRefresh: string): Observable<string> {
    this.logger.info('refreshAccessTokenFromUrl()');
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    let dataToSend: string = "grant_type=refresh_token&client_id=web_app&client_secret=thehouseisonfire&refresh_token=" + tokenRefresh;
    if (!this.environmentService) {
      this.environmentService = this.injector.get(EnvironmentService);
    }
    this.endpointCodeExchange = this.environmentService.getEnvironmentEndpoint['endpointCodeExchange'];
    return new Observable((observer) => {
      this.httpClient.post<ITokens>(this.endpointCodeExchange, dataToSend, { headers: headers })
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(1),
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.complete();
          if (this.httpErrorResponse.status === 401 || this.httpErrorResponse.status ===  403) {
            this.logger.info('refreshAccessTokenFromUrl() httpErrorResponse === ' + this.httpErrorResponse.status);
          } else {
            this.errorService.handleHttpClientResponseError(this.httpErrorResponse, 'POST', '[AuthService] refreshAccessTokenFromUrl()');
          }
        })
      )))
      .subscribe((tokens: ITokens) => {
        this.setStorageData(tokens).subscribe(() => {
          this.storageService.getItemFromLocalStorage(this.AuthAccessToken, 'refreshAccessTokenFromUrl()').subscribe((accessToken: string) => {
            observer.next(accessToken);
            observer.complete();
          });
        });
      });
    });
  }
}